import React, { useState, useRef, DragEvent, ChangeEvent } from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';

interface FileDropAreaProps {
  instructions: string;
  subInstructions?: string;
  buttonText: string;
  allowedFileTypes?: string[];
  onFileDrop: (files: File[]) => void;
}

const FileDropArea: React.FC<FileDropAreaProps> = ({
  onFileDrop,
  instructions,
  subInstructions,
  buttonText,
  allowedFileTypes
}) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle drag events
  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const { files } = e.dataTransfer;
      const fileArray = Array.from(files);
      if (fileArray.length > 10) fileArray.splice(10); // Limit to 10 files
      onFileDrop(fileArray as unknown as File[]);
    }
  };

  // Handle file input change
  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const fileArray = Array.from(e.target.files);
      if (fileArray.length > 10) fileArray.splice(10); // Limit to 10 files
      onFileDrop(fileArray);
    }
  };

  // Handle button click to open file dialog
  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1,
        mb: 1,
        border: isDragging ? '2px dashed #666' : '2px dashed #ccc',
        borderRadius: 2,
        backgroundColor: isDragging ? '#e8e8e8' : '#fafafa',
        transition: 'all 0.3s ease',
        cursor: 'default',
        opacity: 1,
        '& .MuiPaper-root': {
          boxShadow: 'none'
        }
      }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}>
      <input
        type="file"
        multiple
        accept={allowedFileTypes?.join(',')}
        ref={fileInputRef}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 1,
          paddingTop: 2,
          paddingBottom: 2
        }}>
        <Typography
          variant="inherit"
          gutterBottom>
          {instructions}
        </Typography>
        <Button
          variant="contained"
          component="span"
          size="small"
          sx={{
            mt: 1,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none'
            }
          }}
          onClick={handleButtonClick}>
          {buttonText}
        </Button>
        <Typography
          variant="inherit"
          sx={{
            mt: 1,
            fontSize: '0.75rem'
          }}
          gutterBottom>
          {subInstructions}
        </Typography>
      </Box>
    </Paper>
  );
};

export default FileDropArea;
